import { P2GCheckBoxLegacy, P2GTextField, P2GDropdown, P2GText, P2GBox } from '@parcel2go/core-ui';
import { P2GDatePicker } from '@parcel2go/core-ui/datepicker';
import { useMemo, useState } from 'react';
import { ActionType, IntergrationInstructionInput } from '../../../lib/api/account-linking/types';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import S3PresignedImageUploader from '~/apps/self-serve/pages/OpenEnquiry/stages/S3PresignedImageUploader';
import { getInputOptions } from '../lib/helpers/StepInputOptions';
import { validateInput } from '../lib/helpers/ValidateIntegration';

interface props {
  input: IntergrationInstructionInput;
  value: string;
  disabled: boolean;
  onUpdate: (key: string, value: string) => void;
  label?: React.ReactNode;
  integrationType: string;
  integrationSlug: string;
  action: ActionType;
  onFileUploadSubmission?: () => void;
}

export default function StepInput(props: props) {
  const { t } = useTranslation(TranslationNamespaces.ACCOUNT_LINKING);
  const onUpdate = (val: string) => props.onUpdate(props.input.propertyName, val);
  const label = props.input.placeholder;
  const [touched, setTouched] = useState(false);
  const errorMessage = validateInput(props.input, props.value);
  const showError = touched && !!errorMessage;

  // Combine options into one list. Defaults are seeded by the parent form
  // (ScreenSteps/Steps) so that validation and the visible value stay in sync.
  const options = useMemo(() => getInputOptions(props.input), [props.input]);

  if (props.input.valueType === 'DateTime') {
    return (
      <P2GDatePicker
        variant="dialog"
        onChange={(date) => onUpdate(date.toISOString())}
        value={props.value ? new Date(props.value) : null}
        datePickerProps={{ label, slotProps: { textField: { fullWidth: true } } }}
      />
    );
  }

  // Swap display based on input type
  switch (props.input.type) {
    case 'ExternalFile':
      return (
        <P2GBox pb={2}>
          <S3PresignedImageUploader
            translationNamespace={TranslationNamespaces.ACCOUNT_LINKING}
            acceptedFileTypes={
              props.integrationType === 'KnowYourCustomer'
                ? ['application/pdf', 'image/jpeg']
                : [
                    'image/png',
                    'image/jpeg',
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                  ]
            }
            presignedUrl={{ url: props.input.value }}
            onFileUploaded={(f) => {
              for (const file of f) {
                onUpdate(file);
              }
            }}
            buttonFullWidth
            buttonVariant="tertiary"
            buttonSize="large"
            onSubmit={props.onFileUploadSubmission}
            requestedFiles={props.input.optionGroups?.flatMap((g) => g.options.map((o) => o.key))}
          />
        </P2GBox>
      );

    case 'MultipleAnswer':
    case 'MultipleChoice':
      return (
        <P2GDropdown
          disabled={props.disabled}
          fullWidth
          label={t(`${props.integrationSlug}_${props.action}_${props.input.propertyName}_INPUT_LABEL`.toUpperCase(), {
            defaultValue: label
          })}
          id={props.input.propertyName}
          value={props.value}
          options={options}
          onChange={onUpdate}
        />
      );

    case 'TrueOrFalse':
      return (
        <P2GCheckBoxLegacy
          disabled={props.disabled}
          label={props.label || <P2GText variant="body2">{label}</P2GText>}
          id={props.input.propertyName}
          checked={props.value?.toLowerCase() === 'true'}
          onChange={(v) => onUpdate(v ? 'true' : 'false')}
        />
      );

    default:
      return (
        <P2GTextField
          disabled={props.disabled}
          fullWidth
          label={t(`${props.integrationSlug}_${props.action}_${props.input.propertyName}_INPUT_LABEL`.toUpperCase(), {
            defaultValue: label
          })}
          id={props.input.propertyName}
          value={props.value ?? ''}
          required={props.input.validationRules.required}
          placeholder={props.input.placeholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setTouched(true);
            onUpdate(e.currentTarget.value);
          }}
          onBlur={() => setTouched(true)}
          error={showError}
          errorText={showError ? errorMessage : undefined}
          pattern={props.input.validationRules.pattern || undefined}
          maxLength={props.input.validationRules.maxLength || undefined}
        />
      );
  }
}
